import { Link, useSearchParams } from "react-router-dom";
import { Plus, Timer, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Empty } from "@/components/common";
import { TONES } from "@/components/dashboard/parts";
import { daysSince } from "@/components/WorkshopPanel";
import { useList } from "@/lib/hooks";
import { WORKSHOP_STAGES, STAGE_TONE, fmtDate, todayStr } from "@/lib/constants";

const FILTERS = [["workshop", "Tous"], ["workshop_diag", "À diagnostiquer"], ["workshop_wait", "Attente pièce/accord"], ["workshop_repair", "En réparation"], ["workshop_ready", "Prêts"], ["workshop_returned", "Restitués"]];

const Card = ({ i }) => {
  const ws = i.workshop || {};
  const t = TONES[STAGE_TONE[ws.stage] || "grey"];
  const late = ws.expected_date && ws.expected_date < todayStr() && !["pret", "restitue"].includes(ws.stage);
  return (
    <li className={`min-w-0 rounded-xl border-l-2 ${t.bar} border border-white/[0.07] bg-[#122549]/60 p-3`} data-testid={`workshop-card-${i.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-white">{i.client_name || "—"}</p>
          <p className="truncate text-sm text-slate-400">{i.title}</p>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs ${t.soft} ${t.text}`}>{WORKSHOP_STAGES[ws.stage]}</span>
      </div>
      <p className="mt-2 text-xs text-slate-400">
        {ws.deposit_number} · {ws.stage === "restitue" ? `restitué le ${fmtDate(ws.returned_at)}` : `${daysSince(ws.deposit_date)} j en atelier`}
        {ws.expected_date && ws.stage !== "restitue" && <span className={late ? "text-amber-300" : ""}> · prévu le {fmtDate(ws.expected_date)}</span>}
        {ws.stage === "pret" && <span className="text-emerald-300"> · prêt depuis {daysSince(ws.ready_at)} j</span>}
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button asChild variant="outline" className="h-11"><Link to={`/interventions/${i.id}`} data-testid={`workshop-open-${i.id}`}>Fiche<ChevronRight className="ml-1 h-4 w-4" /></Link></Button>
        {!["pret", "restitue"].includes(ws.stage) && <Button asChild className="h-11"><Link to={`/interventions/${i.id}?chrono=1`} data-testid={`workshop-timer-${i.id}`}><Timer className="mr-1 h-4 w-4" />Chrono</Link></Button>}
      </div>
    </li>
  );
};

export default function Workshop() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "workshop";
  const { items } = useList("/interventions", { category: cat });
  return (
    <>
      <PageHeader title="Atelier" subtitle="Matériel déposé : diagnostic, réparation et restitution">
        <Button asChild className="dt-glow"><Link to="/interventions/nouvelle?mode=atelier" data-testid="workshop-new-button"><Plus className="mr-1 h-4 w-4" />Nouveau dépôt</Link></Button>
      </PageHeader>
      <div className="flex flex-wrap gap-2" data-testid="workshop-filters">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setParams({ cat: k })} data-testid={`workshop-filter-${k}`}
            className={`rounded-full border px-3 py-2 text-sm ${cat === k ? "border-cyan-400 bg-cyan-400/15 text-cyan-100" : "border-white/10 text-slate-400"}`}>{label}</button>
        ))}
      </div>
      {!items.length ? <Empty testid="workshop-empty">Aucun matériel dans cette catégorie.</Empty>
        : <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3" data-testid="workshop-list">{items.map((i) => <Card key={i.id} i={i} />)}</ul>}
    </>
  );
}
